import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { resolveImageUrl } from "../lib/imageUrl";
import { ArrowRight, Sprout, Truck, ShieldCheck, MessageCircle } from "lucide-react";
import GalleryShowcase from "../components/GalleryShowcase";
import HeroCarousel from "../components/HeroCarousel";

export default function Home() {
  const { t, lang } = useApp();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const wa = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hello, I'm interested in your rice products."
  )}`;

  return (
    <div data-testid="home-page" className="bg-[#fbf8f1]">
      {/* HERO CAROUSEL — auto-rotating banner slides, falls back to static if no slides uploaded */}
      <HeroCarousel />

      {/* WHY US */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="max-w-2xl mb-14">
          <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold mb-3">
            {lang === "en" ? "Why buyers trust us" : "విశ్వాసనీయత"}
          </div>
          <h2 className={`text-3xl sm:text-4xl tracking-tight font-semibold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
            {t.home.whyTitle}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { id: "fresh", icon: Sprout, title: t.home.why1Title, desc: t.home.why1Desc },
            { id: "wholesale", icon: ShieldCheck, title: t.home.why2Title, desc: t.home.why2Desc },
            { id: "delivery", icon: Truck, title: t.home.why3Title, desc: t.home.why3Desc },
          ].map((f) => (
            <div
              key={f.id}
              className="p-8 bg-[#f2ebd9] border border-stone-200 rounded-sm hover:-translate-y-1 hover:shadow-lg transition-all"
              data-testid={`feature-card-${f.id}`}
            >
              <div className="w-11 h-11 rounded-sm bg-[#274e13] text-[#e69138] grid place-items-center mb-5">
                <f.icon size={20} />
              </div>
              <h3 className={`text-xl font-medium text-stone-900 mb-2 ${lang === "te" ? "font-telugu" : ""}`}>{f.title}</h3>
              <p className={`text-sm text-stone-600 leading-relaxed ${lang === "te" ? "font-telugu" : ""}`}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section className="bg-[#f2ebd9] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6 mb-12 flex-wrap">
            <div className="max-w-2xl">
              <div className="text-[11px] tracking-[0.2em] uppercase text-[#b45f06] font-semibold mb-3">
                {lang === "en" ? "Catalog" : "క్యాటలాగ్"}
              </div>
              <h2 className={`text-3xl sm:text-4xl tracking-tight font-semibold text-stone-900 ${lang === "te" ? "font-telugu" : ""}`}>
                {t.home.productsTitle}
              </h2>
              <p className={`text-stone-600 mt-3 ${lang === "te" ? "font-telugu" : ""}`}>{t.home.productsSub}</p>
            </div>
            <Link
              data-testid="home-view-all"
              to="/products"
              className="text-sm font-semibold text-[#274e13] hover:underline inline-flex items-center gap-1"
            >
              {t.home.viewAll} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => (
              <Link
                key={p.id}
                data-testid={`home-product-${p.id}`}
                to="/products"
                className="group bg-white rounded-sm overflow-hidden border border-stone-200 hover:shadow-lg transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                  <img src={resolveImageUrl(p.image_url)} alt={p.name_en} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <div className="text-xs text-stone-500 mb-1">{p.name_te}</div>
                  <div className="font-semibold text-stone-900">{p.name_en}</div>
                  <div className="mt-1.5 text-sm text-[#274e13] font-semibold">
                    ₹{p.price_per_kg.toFixed(0)} <span className="font-normal text-stone-500">/ kg</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY SHOWCASE — auto-renders only categories that have uploaded images */}
      <GalleryShowcase />

      {/* CTA */}
      <section className="bg-[#274e13] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <h2 className={`text-3xl sm:text-4xl font-semibold tracking-tight ${lang === "te" ? "font-telugu" : ""}`}>
            {t.home.ctaTitle}
          </h2>
          <p className={`mt-4 text-stone-300 max-w-2xl mx-auto ${lang === "te" ? "font-telugu" : ""}`}>
            {t.home.ctaSub}
          </p>
          <a
            data-testid="home-cta-quote"
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="mt-8 inline-flex items-center gap-2 bg-[#e69138] hover:bg-[#b45f06] text-white px-8 py-3.5 rounded-sm font-semibold tracking-wide transition-colors"
          >
            <MessageCircle size={16} /> {t.home.ctaBtn}
          </a>
        </div>
      </section>
    </div>
  );
}
